import { useState } from 'react'
import PaginationControls, { paginateItems } from '../components/PaginationControls'
import { useApp } from '../context/AppContext'
import { lumi } from '../lib/lumi'
import { persistDeletedVersion, persistDeletedVersions } from '../lib/lumiAppendOnly'
import { formatCurrency, gerarCodigo, nowISO } from '../utils'
import type { Aprovacao, ItemRequisicao, Requisicao } from '../types'

const STATUS_COLOR: Record<string, string> = {
  RASCUNHO: '#94a3b8',
  ENVIADA: '#60a5fa',
  EM_APROVACAO: '#fbbf24',
  APROVADA: '#4ade80',
  REJEITADA: '#f87171',
  EM_COTACAO: '#a78bfa',
  CONVERTIDA_OC: '#34d399',
  CANCELADA: '#6b7280',
}

type ItemForm = {
  item_orcado_id: string
  quantidade: number
  valor_unitario_estimado: number
}

type IdentifiableRecord = {
  _id?: string
  id?: string
}

const APP_CREATOR_ID = 'app-4s'
const APP_CREATOR_EMAIL = 'sistema@app4s.local'

function refs(record: IdentifiableRecord | undefined) {
  return [record?.id, record?._id].filter(Boolean).map(String)
}

function sameRef(left: string | undefined, right: string | undefined, records: IdentifiableRecord[] = []) {
  if (!left || !right) return false
  if (left === right) return true

  return records.some(record => {
    const recordRefs = refs(record)
    return recordRefs.includes(left) && recordRefs.includes(right)
  })
}

function findByRef<T extends IdentifiableRecord>(records: T[], ref: string | undefined) {
  if (!ref) return undefined
  return records.find(record => refs(record).includes(ref))
}

function removeUndefinedFields<T extends Record<string, unknown>>(payload: T): T {
  return Object.fromEntries(
    Object.entries(payload).filter(([, value]) => value !== undefined),
  ) as T
}

async function persistAppendOnly(entityName: string, record: Record<string, unknown>) {
  const entity = (lumi.entities as Record<string, { create: (data: Record<string, unknown>) => Promise<unknown> }>)[entityName]
  await entity.create(removeUndefinedFields({
    ...record,
    creator: record.creator ?? APP_CREATOR_ID,
    creatorEmail: record.creatorEmail ?? APP_CREATOR_EMAIL,
    updatedAt: nowISO(),
  }))
}

export default function Requisicoes() {
  const {
    requisicoes, setRequisicoes, itensRequisicao, setItensRequisicao, obras, centrosCusto,
    itensOrcados, aprovacoes, setAprovacoes, currentUser, addLog,
  } = useApp()
  const [search, setSearch] = useState('')
  const [filterObra, setFilterObra] = useState('')
  const [modal, setModal] = useState(false)
  const [editId, setEditId] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [sendingApprovalId, setSendingApprovalId] = useState<string | null>(null)
  const [toast, setToast] = useState('')
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [form, setForm] = useState({
    obra_id: '',
    centro_custo_id: '',
    solicitante_nome: currentUser?.nome || '',
    data_solicitacao: new Date().toISOString().slice(0, 10),
    tipo_requisicao: 'MATERIAL' as Requisicao['tipo_requisicao'],
    justificativa: '',
    observacoes: '',
  })
  const [itemRows, setItemRows] = useState<ItemForm[]>([{ item_orcado_id: '', quantidade: 1, valor_unitario_estimado: 0 }])

  const filtered = requisicoes.filter(r =>
    (filterObra ? sameRef(r.obra_id, filterObra, obras) : true) &&
    (r.numero_requisicao.toLowerCase().includes(search.toLowerCase()) || r.solicitante_nome.toLowerCase().includes(search.toLowerCase()))
  )
  const centrosFiltrados = centrosCusto.filter(cc => sameRef(cc.obra_id, form.obra_id, obras))
  const paginatedRequisicoes = paginateItems(filtered, page, pageSize)
  const itensDisponiveis = itensOrcados.filter(i =>
    sameRef(i.obra_id, form.obra_id, obras) &&
    (!form.centro_custo_id || !i.centro_custo_id || sameRef(i.centro_custo_id, form.centro_custo_id, centrosCusto)) &&
    i.status === 'ATIVO'
  )

  function showToast(msg: string) {
    setToast(msg)
    setTimeout(() => setToast(''), 3000)
  }

  function resetModal() {
    setEditId(null)
    setForm({
      obra_id: '',
      centro_custo_id: '',
      solicitante_nome: currentUser?.nome || '',
      data_solicitacao: new Date().toISOString().slice(0, 10),
      tipo_requisicao: 'MATERIAL',
      justificativa: '',
      observacoes: '',
    })
    setItemRows([{ item_orcado_id: '', quantidade: 1, valor_unitario_estimado: 0 }])
    setErrors({})
    setModal(true)
  }

  function openEdit(requisicao: Requisicao) {
    setEditId(requisicao.id)
    setForm({
      obra_id: requisicao.obra_id,
      centro_custo_id: requisicao.centro_custo_id,
      solicitante_nome: requisicao.solicitante_nome,
      data_solicitacao: requisicao.data_solicitacao,
      tipo_requisicao: requisicao.tipo_requisicao,
      justificativa: requisicao.justificativa,
      observacoes: requisicao.observacoes || '',
    })
    setItemRows(
      itensDaReq(requisicao).map(item => ({
        item_orcado_id: item.item_orcado_id,
        quantidade: item.quantidade,
        valor_unitario_estimado: item.valor_unitario_estimado,
      })),
    )
    setErrors({})
    setModal(true)
  }

  function itemTotal(row: ItemForm) {
    return (Number(row.quantidade) || 0) * (Number(row.valor_unitario_estimado) || 0)
  }

  function itensDaReq(requisicao: Requisicao) {
    return itensRequisicao.filter(i => sameRef(i.requisicao_id, requisicao.id, [requisicao as unknown as IdentifiableRecord]))
  }

  function validate() {
    const e: Record<string, string> = {}
    if (!form.obra_id) e.obra_id = 'Obra obrigatoria'
    if (!form.centro_custo_id) e.centro_custo_id = 'Centro de custo obrigatorio'
    if (!form.justificativa.trim()) e.justificativa = 'Justificativa obrigatoria'
    if (itemRows.length === 0 || itemRows.some(i => !i.item_orcado_id || !i.quantidade || !i.valor_unitario_estimado)) {
      e.itens = 'Informe ao menos um item orcado, quantidade e valor unitario.'
    }
    return e
  }

  async function save() {
    const e = validate()
    if (Object.keys(e).length) {
      setErrors(e)
      return
    }

    setSaving(true)
    const reqAtual = editId ? requisicoes.find(item => item.id === editId) : null
    const reqId = reqAtual?.id || 'req' + Date.now()
    const novosItens: ItemRequisicao[] = itemRows.map((row, index) => {
      const itemOrcado = findByRef(itensOrcados, row.item_orcado_id)
      const total = itemTotal(row)
      const saldo = itemOrcado?.saldo_atual ?? 0
      return {
        id: `${editId ? 'ir-edit' : 'ir'}${Date.now()}${index}`,
        requisicao_id: reqId,
        item_orcado_id: itemOrcado?.id ?? row.item_orcado_id,
        descricao: itemOrcado?.nome_item || 'Item requisitado',
        unidade_medida: itemOrcado?.unidade_medida || '',
        quantidade: Number(row.quantidade) || 0,
        valor_unitario_estimado: Number(row.valor_unitario_estimado) || 0,
        valor_total_estimado: total,
        saldo_disponivel_no_momento: saldo,
        excede_orcamento: total > saldo,
        createdAt: nowISO(),
      }
    })
    const valorTotal = novosItens.reduce((s, item) => s + item.valor_total_estimado, 0)
    const excede = novosItens.some(item => item.excede_orcamento)
    const requisicaoSalva: Requisicao = {
      ...reqAtual,
      id: reqId,
      numero_requisicao: reqAtual?.numero_requisicao || gerarCodigo(),
      ...form,
      status: reqAtual?.status || 'RASCUNHO',
      valor_total_estimado: valorTotal,
      excede_orcamento: excede,
      bloqueada_por_orcamento: excede,
      createdAt: reqAtual?.createdAt || nowISO(),
    }

    try {
      await persistAppendOnly('requisicoes', requisicaoSalva as unknown as Record<string, unknown>)
      await Promise.all(novosItens.map(item => persistAppendOnly('itens_requisicao', item as unknown as Record<string, unknown>)))
      setRequisicoes(prev => editId ? prev.map(item => item.id === editId ? requisicaoSalva : item) : [...prev, requisicaoSalva])
      setItensRequisicao(prev => [...prev.filter(item => !sameRef(item.requisicao_id, reqId, [requisicaoSalva as unknown as IdentifiableRecord])), ...novosItens])
      addLog('REQUISICAO', requisicaoSalva.id, editId ? 'EDITADO' : 'CRIADO', `Requisicao ${requisicaoSalva.numero_requisicao} ${editId ? 'atualizada' : 'criada'} com ${novosItens.length} item(ns)`)
      showToast(excede ? 'Requisicao salva, mas bloqueada por saldo orcamentario.' : editId ? 'Requisicao atualizada.' : 'Requisicao criada com sucesso.')
      setModal(false)
      setEditId(null)
    } catch (error) {
      console.error('Falha ao confirmar requisicao no Lumi:', error)
      showToast('Erro, tente novamente mais tarde.')
    } finally {
      setSaving(false)
    }
  }

  async function deleteRequisicao(requisicao: Requisicao) {
    const confirmed = window.confirm(`Excluir a requisicao ${requisicao.numero_requisicao}? Esta acao remove tambem os itens e aprovacoes vinculados.`)
    if (!confirmed) return

    setSaving(true)
    try {
      const itensVinculados = itensDaReq(requisicao)
      const aprovacoesVinculadas = aprovacoes.filter(item => item.entidade_tipo === 'REQUISICAO' && item.entidade_id === requisicao.id)
      await persistDeletedVersions('itens_requisicao', itensVinculados as unknown as Record<string, unknown>[])
      await persistDeletedVersions('aprovacoes', aprovacoesVinculadas as unknown as Record<string, unknown>[])
      await persistDeletedVersion('requisicoes', requisicao as unknown as Record<string, unknown>)
    } catch {
      showToast('Erro, tente novamente mais tarde.')
      setSaving(false)
      return
    }
    setRequisicoes(prev => prev.filter(item => item.id !== requisicao.id))
    setItensRequisicao(prev => prev.filter(item => !sameRef(item.requisicao_id, requisicao.id, [requisicao as unknown as IdentifiableRecord])))
    setAprovacoes(prev => prev.filter(item => !(item.entidade_tipo === 'REQUISICAO' && item.entidade_id === requisicao.id)))
    addLog('REQUISICAO', requisicao.id, 'CANCELADO', `Requisicao ${requisicao.numero_requisicao} excluida`)
    showToast('Requisicao excluida.')
    setSaving(false)
  }

  async function criarAprovacao(r: Requisicao) {
    if (r.bloqueada_por_orcamento) {
      showToast('Requisicao bloqueada por orcamento. Ajuste os itens antes de enviar.')
      return
    }

    if (sendingApprovalId) return

    const jaExiste = aprovacoes.some(a => a.entidade_tipo === 'REQUISICAO' && a.entidade_id === r.id && a.status === 'PENDENTE')
    const requisicaoEmAprovacao = { ...r, status: 'EM_APROVACAO' as Requisicao['status'] }
    const nova: Aprovacao | null = jaExiste ? null : {
      id: 'ap' + Date.now(),
      entidade_tipo: 'REQUISICAO',
      entidade_id: r.id,
      etapa_aprovacao: 'ENGENHARIA',
      aprovador_nome: '',
      perfil_aprovador: 'ENGENHARIA',
      solicitante_usuario_id: currentUser?.id,
      solicitante_nome: currentUser?.nome || r.solicitante_nome,
      solicitante_perfil: currentUser?.perfil,
      status: 'PENDENTE',
      createdAt: nowISO(),
    }

    setSendingApprovalId(r.id)
    try {
      await persistAppendOnly('requisicoes', requisicaoEmAprovacao as unknown as Record<string, unknown>)
      if (nova) {
        await persistAppendOnly('aprovacoes', nova as unknown as Record<string, unknown>)
      }

      if (nova) {
        setAprovacoes(prev => [nova, ...prev])
      }
      setRequisicoes(prev => prev.map(x => x.id === r.id ? requisicaoEmAprovacao : x))
      addLog('REQUISICAO', r.id, 'EDITADO', `Requisicao ${r.numero_requisicao} enviada para aprovacao`)
      showToast('Requisicao enviada para aprovacao.')
    } catch (error) {
      console.error('Falha ao enviar requisicao para aprovacao no Lumi:', error)
      showToast('Erro, tente novamente mais tarde.')
    } finally {
      setSendingApprovalId(null)
    }
  }

  function liberarAprovada(r: Requisicao) {
    const aprovada = aprovacoes.some(a => a.entidade_tipo === 'REQUISICAO' && a.entidade_id === r.id && a.status === 'APROVADO')
    const rejeitada = aprovacoes.some(a => a.entidade_tipo === 'REQUISICAO' && a.entidade_id === r.id && a.status === 'REJEITADO')
    if (rejeitada) {
      setRequisicoes(prev => prev.map(x => x.id === r.id ? { ...x, status: 'REJEITADA' } : x))
      showToast('Requisicao rejeitada no workflow.')
      return
    }
    if (!aprovada) {
      showToast('Aguardando aprovacao da Engenharia.')
      return
    }
    setRequisicoes(prev => prev.map(x => x.id === r.id ? { ...x, status: 'APROVADA' } : x))
    showToast('Requisicao liberada para cotacao.')
  }

  const obraName = (id: string) => findByRef(obras, id)?.nome_obra || '-'
  const ccName = (id: string) => findByRef(centrosCusto, id)?.nome || '-'
  const itemName = (id: string) => findByRef(itensOrcados, id)?.nome_item || '-'

  return (
    <div>
      {toast && <div style={{ position: 'fixed', top: 20, right: 24, background: '#22c55e', color: '#fff', padding: '10px 18px', borderRadius: 8, fontSize: 13, zIndex: 9999, fontWeight: 500 }}>{toast}</div>}

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <h1 style={{ color: '#f7f8f8', fontSize: 18, fontWeight: 600, margin: 0 }}>Requisicoes</h1>
          <p style={{ color: '#62666d', fontSize: 13, margin: '2px 0 0' }}>{requisicoes.length} requisicoes</p>
        </div>
        <button onClick={resetModal} style={{ background: '#f0403b', color: '#fff', border: 'none', borderRadius: 7, padding: '8px 16px', fontSize: 13, cursor: 'pointer', fontWeight: 500 }}>+ Nova Requisicao</button>
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar..." style={{ background: '#1c1d24', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 7, padding: '8px 12px', color: '#f7f8f8', fontSize: 13, width: 240, outline: 'none' }} />
        <select value={filterObra} onChange={e => setFilterObra(e.target.value)} style={{ background: '#1c1d24', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 7, padding: '8px 12px', color: '#f7f8f8', fontSize: 13, outline: 'none' }}>
          <option value="">Todas as obras</option>
          {obras.map(o => <option key={o.id} value={o.id}>{o.nome_obra}</option>)}
        </select>
      </div>

      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead><tr style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          {['Numero', 'Obra', 'Centro Custo', 'Itens', 'Valor Est.', 'Status', 'Acoes'].map(h => <th key={h} style={{ textAlign: 'left', padding: '8px 12px', color: '#62666d', fontWeight: 500 }}>{h}</th>)}
        </tr></thead>
        <tbody>
          {paginatedRequisicoes.items.map(r => {
            const itens = itensDaReq(r)
            return (
              <tr key={r.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }} className="table-row">
                <td style={{ padding: '10px 12px', color: '#ffb4b1', fontFamily: 'monospace', fontSize: 11 }}>{r.numero_requisicao}</td>
                <td style={{ padding: '10px 12px', color: '#f7f8f8', fontWeight: 500 }}>{obraName(r.obra_id)}</td>
                <td style={{ padding: '10px 12px', color: '#a0a4ab' }}>{ccName(r.centro_custo_id)}</td>
                <td style={{ padding: '10px 12px', color: '#a0a4ab' }}>
                  {itens.length} item(ns)
                  <div style={{ color: '#62666d', fontSize: 11 }}>{itens.slice(0, 2).map(i => itemName(i.item_orcado_id)).join(', ')}</div>
                </td>
                <td style={{ padding: '10px 12px', color: r.excede_orcamento ? '#f87171' : '#4ade80', fontWeight: 600 }}>{formatCurrency(r.valor_total_estimado)}</td>
                <td style={{ padding: '10px 12px' }}>
                  <span style={{ background: (STATUS_COLOR[r.status] || '#94a3b8') + '22', color: STATUS_COLOR[r.status] || '#94a3b8', borderRadius: 5, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>{r.status.replace(/_/g, ' ')}</span>
                  {r.bloqueada_por_orcamento && <span style={{ marginLeft: 6, background: '#f8717122', color: '#f87171', borderRadius: 5, padding: '2px 6px', fontSize: 10, fontWeight: 700 }}>BLOQUEADA</span>}
                </td>
                <td style={{ padding: '10px 12px' }}>
                  <button onClick={() => openEdit(r)} style={{ background: '#1c1d24', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 5, padding: '4px 10px', color: '#ffb4b1', fontSize: 12, cursor: 'pointer', marginRight: 6 }}>Editar</button>
                  {['RASCUNHO', 'ENVIADA'].includes(r.status) && !r.bloqueada_por_orcamento && (
                    <button
                      onClick={() => void criarAprovacao(r)}
                      disabled={sendingApprovalId === r.id}
                      style={{ background: '#1c1d24', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 5, padding: '4px 10px', color: '#ffb4b1', fontSize: 12, cursor: sendingApprovalId === r.id ? 'wait' : 'pointer', opacity: sendingApprovalId === r.id ? 0.65 : 1 }}
                    >
                      {sendingApprovalId === r.id ? 'Enviando...' : 'Enviar aprovacao'}
                    </button>
                  )}
                  {r.status === 'EM_APROVACAO' && (
                    <button onClick={() => liberarAprovada(r)} style={{ background: '#1c1d24', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 5, padding: '4px 10px', color: '#ffb4b1', fontSize: 12, cursor: 'pointer' }}>Verificar aprovacao</button>
                  )}
                  <button onClick={() => deleteRequisicao(r)} style={{ background: '#1c1d24', border: '1px solid rgba(248,113,113,0.25)', borderRadius: 5, padding: '4px 10px', color: '#f87171', fontSize: 12, cursor: 'pointer', marginLeft: 6 }}>Excluir</button>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
      <PaginationControls
        page={paginatedRequisicoes.safePage}
        pageSize={pageSize}
        totalItems={filtered.length}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
      />

      {modal && (
        <div className="modal-overlay" data-testid="requisicao-modal-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.72)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#16171e', border: '1px solid rgba(240,64,59,0.35)', borderRadius: 10, padding: 24, width: 760, maxHeight: '88vh', overflowY: 'auto' }}>
            <h2 style={{ color: '#f7f8f8', fontSize: 16, fontWeight: 600, margin: '0 0 6px' }}>{editId ? 'Editar Requisicao' : 'Nova Requisicao'}</h2>
            <p style={{ color: '#62666d', fontSize: 12, margin: '0 0 18px' }}>Inclua itens vinculados ao orcamento para validar saldo.</p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div>
                <label className="label-field">Obra *</label>
                <select className="input-field" value={form.obra_id} onChange={e => {
                  setForm(p => ({ ...p, obra_id: e.target.value, centro_custo_id: '' }))
                  setItemRows([{ item_orcado_id: '', quantidade: 1, valor_unitario_estimado: 0 }])
                }}>
                  <option value="">Selecione...</option>
                  {obras.map(o => <option key={o.id} value={o.id}>{o.nome_obra}</option>)}
                </select>
                {errors.obra_id && <p style={{ color: '#f87171', fontSize: 11 }}>{errors.obra_id}</p>}
              </div>
              <div>
                <label className="label-field">Centro de custo *</label>
                <select className="input-field" value={form.centro_custo_id} onChange={e => {
                  setForm(p => ({ ...p, centro_custo_id: e.target.value }))
                  setItemRows(prev => prev.map(row => ({ ...row, item_orcado_id: '', valor_unitario_estimado: 0 })))
                }} disabled={!form.obra_id}>
                  <option value="">Selecione...</option>
                  {centrosFiltrados.map(cc => <option key={cc.id} value={cc.id}>{cc.nome}</option>)}
                </select>
                {errors.centro_custo_id && <p style={{ color: '#f87171', fontSize: 11 }}>{errors.centro_custo_id}</p>}
              </div>
              <div>
                <label className="label-field">Tipo</label>
                <select className="input-field" value={form.tipo_requisicao} onChange={e => setForm(p => ({ ...p, tipo_requisicao: e.target.value as Requisicao['tipo_requisicao'] }))}>
                  <option value="MATERIAL">MATERIAL</option>
                  <option value="SERVICO">SERVICO</option>
                  <option value="LOCACAO">LOCACAO</option>
                </select>
              </div>
              <div>
                <label className="label-field">Data</label>
                <input className="input-field" type="date" value={form.data_solicitacao} onChange={e => setForm(p => ({ ...p, data_solicitacao: e.target.value }))} />
              </div>
              <div style={{ gridColumn: 'span 2' }}>
                <label className="label-field">Justificativa *</label>
                <textarea className="input-field" rows={2} value={form.justificativa} onChange={e => setForm(p => ({ ...p, justificativa: e.target.value }))} />
                {errors.justificativa && <p style={{ color: '#f87171', fontSize: 11 }}>{errors.justificativa}</p>}
              </div>
            </div>

            <div style={{ marginTop: 18 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <p style={{ color: '#f7f8f8', fontSize: 13, fontWeight: 600 }}>Itens da requisicao</p>
                <button onClick={() => setItemRows(prev => [...prev, { item_orcado_id: '', quantidade: 1, valor_unitario_estimado: 0 }])} style={{ background: '#1c1d24', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 6, color: '#ffb4b1', padding: '5px 10px', fontSize: 12, cursor: 'pointer' }}>+ Item</button>
              </div>
              {errors.itens && <p style={{ color: '#f87171', fontSize: 11 }}>{errors.itens}</p>}
              {itemRows.map((row, index) => {
                const item = findByRef(itensOrcados, row.item_orcado_id)
                const total = itemTotal(row)
                const excede = item ? total > item.saldo_atual : false
                return (
                  <div key={index} style={{ display: 'grid', gridTemplateColumns: '2fr 0.7fr 0.9fr 0.9fr 0.7fr', gap: 8, alignItems: 'end', marginBottom: 8 }}>
                    <div>
                      <label className="label-field">Item orcado</label>
                      <select className="input-field" value={row.item_orcado_id} onChange={e => {
                        const itemSel = findByRef(itensOrcados, e.target.value)
                        setItemRows(prev => prev.map((it, i) => i === index ? { ...it, item_orcado_id: e.target.value, valor_unitario_estimado: itemSel?.valor_unitario_previsto || 0 } : it))
                      }}>
                        <option value="">Selecione...</option>
                        {itensDisponiveis.map(i => <option key={i.id} value={i.id}>{i.nome_item} - saldo {formatCurrency(i.saldo_atual)}</option>)}
                      </select>
                      {form.obra_id && itensDisponiveis.length === 0 && (
                        <p style={{ color: '#fbbf24', fontSize: 11, marginTop: 4 }}>Nenhum item orcado ativo encontrado para a obra selecionada.</p>
                      )}
                    </div>
                    <div>
                      <label className="label-field">Qtd.</label>
                      <input className="input-field" type="number" value={row.quantidade} onChange={e => setItemRows(prev => prev.map((it, i) => i === index ? { ...it, quantidade: Number(e.target.value) || 0 } : it))} />
                    </div>
                    <div>
                      <label className="label-field">Vlr unit.</label>
                      <input className="input-field" type="number" value={row.valor_unitario_estimado} onChange={e => setItemRows(prev => prev.map((it, i) => i === index ? { ...it, valor_unitario_estimado: Number(e.target.value) || 0 } : it))} />
                    </div>
                    <div>
                      <label className="label-field">Total</label>
                      <div style={{ color: excede ? '#f87171' : '#4ade80', fontSize: 13, fontWeight: 700, paddingBottom: 7 }}>{formatCurrency(total)}</div>
                    </div>
                    <button onClick={() => setItemRows(prev => prev.filter((_, i) => i !== index))} style={{ background: '#2a1111', border: '1px solid rgba(248,113,113,0.25)', borderRadius: 6, color: '#f87171', padding: '7px', fontSize: 12, cursor: 'pointer' }}>remover</button>
                  </div>
                )
              })}
              <div style={{ marginTop: 8, color: '#f7f8f8', fontSize: 13, fontWeight: 700 }}>Total: {formatCurrency(itemRows.reduce((s, row) => s + itemTotal(row), 0))}</div>
            </div>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 24 }}>
              <button onClick={() => setModal(false)} className="btn-ghost">Cancelar</button>
              <button onClick={save} className="btn-primary" disabled={saving}>{saving ? 'Salvando...' : editId ? 'Salvar alteracoes' : 'Criar Requisicao'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

